import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Check, Package } from "lucide-react"
import React from "react"
import { DeleteIconButton } from "../shared/DeleteIconButton"
import { HorizontalCard } from "../shared/HorizontalCard"

type MappingStepCard = {
  id: string
  title: string
  subtitle: string
  status: "active" | "complete" | "pending"
  tone: "file" | "review"
}

interface MasterDataMappingStepCardsProps {
  stepCards: MappingStepCard[]
  onStepSelect: (stepId: string) => void
  onDeleteFile?: (stepId: string) => void
  deletingFileId?: string | null
}

export function MasterDataMappingStepCards({
  stepCards,
  onStepSelect,
  onDeleteFile,
  deletingFileId,
}: MasterDataMappingStepCardsProps) {
  const { translate } = useTranslation()

  return (
    <div className="flex items-center gap-0">
      {stepCards.map((step, index) => {
        const isActive = step.status === "active"
        const isComplete = step.status === "complete"
        const prevComplete =
          index > 0 && stepCards[index - 1].status === "complete"
        const isFileStep = step.tone === "file"
        const isDeleting = deletingFileId === step.id

        const icon = isComplete ? (
          <Check className="h-[10px] w-[10px]" />
        ) : step.tone === "review" ? (
          <Package className="h-[10px] w-[10px]" />
        ) : (
          index + 1
        )

        return (
          <React.Fragment key={step.id}>
            {index > 0 && (
              <div
                className={cn(
                  "h-px w-7 shrink-0 transition-colors",
                  prevComplete ? "bg-yellow2" : "bg-grey4",
                )}
              />
            )}
            <div className="group relative">
              <HorizontalCard
                icon={icon}
                title={step.title}
                subtitle={step.subtitle}
                size="compact"
                isActive={isActive}
                isComplete={isComplete}
                onClick={() => onStepSelect(step.id)}
              />
              {isFileStep && onDeleteFile && (
                <DeleteIconButton
                  label={translate("MC_MD_MAP_STEP_REMOVE_FILE", {
                    name: step.title,
                  })}
                  loading={isDeleting}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteFile(step.id)
                  }}
                  className="-right-1.5 -top-1.5 hidden group-hover:flex"
                />
              )}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export type { MappingStepCard }
